import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { ReactNode } from "react";

interface PageTransitionProps {
  children: ReactNode;
  mode?: "fade" | "slide" | "fade-slide";
  duration?: number;
}

const pageVariants = {
  fade: {
    initial: { opacity: 0 },
    enter: { opacity: 1 },
    exit: { opacity: 0 },
  },
  slide: {
    initial: { x: 20, opacity: 0 },
    enter: { x: 0, opacity: 1 },
    exit: { x: -20, opacity: 0 },
  },
  "fade-slide": {
    initial: { y: 20, opacity: 0 },
    enter: { y: 0, opacity: 1 },
    exit: { y: -20, opacity: 0 },
  },
};

export const PageTransition = ({ children, mode = "fade-slide", duration = 0.3 }: PageTransitionProps) => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={pageVariants[mode].initial}
        animate={pageVariants[mode].enter}
        exit={pageVariants[mode].exit}
        transition={{
          duration,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Pure CSS loading fallback - no state updates / re-renders
export const PageLoadingFallback = () => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background animate-fade-in">
      {/* CSS-only progress bar */}
      <div className="mb-8 h-1 w-64 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-gradient-to-r from-primary via-jonquil to-primary"
          style={{
            animation: "loading-progress 2s ease-in-out infinite",
          }}
        />
      </div>

      {/* Spinner */}
      <div className="relative">
        <div className="h-16 w-16 animate-spin rounded-full border-4 border-muted border-t-primary"></div>
        <div className="absolute inset-0 animate-pulse">
          <div className="h-full w-full rounded-full bg-primary/10"></div>
        </div>
      </div>

      {/* Loading text */}
      <p className="mt-6 text-sm text-muted-foreground spinner-fade">
        Loading...
      </p>

      <style>{`
        @keyframes loading-progress {
          0% { width: 0%; }
          50% { width: 90%; }
          100% { width: 90%; }
        }
      `}</style>
    </div>
  );
};
